import {
  Component,
  Element,
  EventEmitter,
  Host,
  Prop,
  State,
  Event as StencilEvent,
  Watch,
  h
} from '@stencil/core';
import { If } from '@utils/If';
import clsx from 'clsx';
import { MenuItem } from '../../shared/types';

@Component({
  tag: 'sub-menu-list',
  styleUrl: 'sub-menu-list.scss',
  shadow: true
})
export class SubMenuList {
  // to query the rendered popup
  @Element() el!: HTMLElement;

  /** Props */
  @Prop() activeRoute: string = '';
  @Prop() parentRect!: DOMRect;
  @Prop() isActive: boolean = false;
  @Prop() menuList: MenuItem[] = [];
  @Prop() submenuIndex: number = null;

  /** Events */
  @StencilEvent() submenuClick!: EventEmitter<any>;

  /** Internal state */
  @State() submenuindexState: number = null;
  private closeTimeout: ReturnType<typeof setTimeout>;

  /** Animation flags */
  private animateLeaveRight = false;
  private animateLeaveLeft = false;
  private animateInRight = false;
  private animateInLeft = false;

  @Watch('submenuIndex')
  handleOpenChange(newVal: number, oldVal: number) {
    clearTimeout(this.closeTimeout);

    if (newVal > oldVal) {
      this.animateLeaveRight = false;
      this.animateLeaveLeft = true;
      this.animateInRight = true;
      this.animateInLeft = false;
    } else if (newVal < oldVal) {
      this.animateLeaveRight = true;
      this.animateLeaveLeft = false;
      this.animateInRight = false;
      this.animateInLeft = true;
    }

    this.closeTimeout = setTimeout(() => {
      this.animateLeaveRight = this.animateLeaveLeft = false;
      this.submenuindexState = newVal;
    }, 250);
  }

  componentWillLoad() {
    this.submenuindexState = this.submenuIndex;
  }

  componentDidLoad() {
    // reposition on window resize
    window.addEventListener('resize', this.positionPopupBound);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.positionPopupBound);
  }

  componentDidRender() {
    // after each render, adjust position & maxHeight
    this.positionPopup();
  }

  // bound so we can removeEventListener
  private positionPopupBound = () => this.positionPopup();

  /** after render (and on resize): re-center & do vertical flip/scroll */
  private positionPopup() {
    const popup = this.el.shadowRoot!.querySelector<HTMLElement>('.submenu-popup');
    if (!popup || !this.parentRect) return;

    const padding = 16;
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const rect = popup.getBoundingClientRect();
    const popupW = rect.width;
    const triggerCenter = this.parentRect.left + this.parentRect.width / 2;

    // 1) Center horizontally under trigger
    let left = triggerCenter - popupW / 2;
    left = Math.max(padding, Math.min(left, screenW - popupW - padding));
    popup.style.left = `${Math.round(left)}px`;

    // 2) Vertical flip/scroll logic (same as before)
    const spaceBelow = screenH - this.parentRect.bottom - padding;
    const spaceAbove = this.parentRect.top - padding;
    let top: number, maxHeight: number;

    if (rect.height <= spaceBelow) {
      top = this.parentRect.bottom;
      maxHeight = spaceBelow;
    } else if (rect.height <= spaceAbove) {
      top = this.parentRect.top - rect.height;
      maxHeight = spaceAbove;
    } else if (spaceBelow >= spaceAbove) {
      top = this.parentRect.bottom;
      maxHeight = spaceBelow;
    } else {
      maxHeight = spaceAbove;
      top = this.parentRect.top - maxHeight;
    }

    // clamp vertically so it never creeps above padding
    top = Math.max(padding, top);
    popup.style.top = `56px`;
    popup.style.maxHeight = `${Math.round(maxHeight)}px`;
    popup.style.overflowY = 'auto';

    // 3) Re-position the arrow if you have one
    const arrow = this.el.shadowRoot!.querySelector<HTMLElement>('.submenu-arrow');
    if (arrow) {
      // arrowWidth/2 = half the arrow graphic’s width, e.g. 6px
      const halfArrow = arrow.offsetWidth / 2;
      // arrowLeft = distance from popup’s left edge
      const arrowLeft = triggerCenter - left - halfArrow;
      arrow.style.left = `${Math.round(arrowLeft)}px`;
    }
  }


  /** initial styles: fixed, max-width, rounded top/left, centered under trigger */
  private getPopupStyle(): Record<string, string> {
    if (!this.parentRect) return {};

    const padding = 16;
    const screenW = window.innerWidth;
    // maxWidth = viewport minus padding on both sides
    const maxWidth = screenW - padding * 2;
    // where is center of trigger?
    const triggerCenter = this.parentRect.left + this.parentRect.width / 2;
    // get actual popup width
    const popupWidth = this.el.getBoundingClientRect().width || maxWidth;
    // initial left = center the popup under the trigger
    let left = triggerCenter - popupWidth / 2;
    // clamp so we never go off-screen
    left = Math.max(padding, Math.min(left, screenW - popupWidth - padding));

    return {
      left: `${Math.round(left)}px`,
      maxWidth: `${maxWidth}px`
    };
  }
  private chunkIntoRows(items: any[], maxPerRow: number): any[][] {
    const rows = [];
    for (let i = 0; i < items.length; i += maxPerRow) {
      rows.push(items.slice(i, i + maxPerRow));
    }
    return rows;
  }

  private groupByCategory(links: MenuItem[]) {
    const grouped = new Map<string, MenuItem[]>();
    const uncategorized: MenuItem[] = [];

    for (const link of links) {
      const categories = Array.isArray(link.categories) ? link.categories : [link.categories];
      let added = false;

      for (const category of categories) {
        const name = category?.name?.trim();
        if (name) {
          if (!grouped.has(name)) grouped.set(name, []);
          grouped.get(name)!.push(link);
          added = true;
        }
      }
      if (!added) {
        uncategorized.push(link);
      }
    }

    return { grouped, uncategorized };
  }

  render() {
    const idx = this.submenuindexState;
    if (idx == null || idx < 0 || idx >= this.menuList.length) {
      return null;
    }

    const menuItem = this.menuList[idx];
    const headerLinks = menuItem.header_links || [];
    const { grouped } = this.groupByCategory(headerLinks);
    const isGrouped = grouped.size > 0;
    const rows = this.chunkIntoRows([...grouped.entries()], 5);

    return (
      <Host>
        <div
          class="submenu-popup no-scrollbar"
          style={this.getPopupStyle()}
        >
          <div class="flex flex-no-wrap overflow-scroll transition-all no-scrollbar min-w-52">
            {this.menuList.map((mi, i) => (
              <If
                condition={i === idx}
                children={
                  <div
                    key={i}
                    class={`shrink-0 grow-1 ${clsx({
                      'slide-in-right': this.animateInRight,
                      'slide-in-left': this.animateInLeft,
                      'slide-out-left': this.animateLeaveLeft,
                      'slide-out-right': this.animateLeaveRight
                    })}`}
                  >
                    <If
                      condition={!!mi.submenuTitle}
                      children={<span class="font-semibold px-4 py-3 block">{mi.submenuTitle}</span>}
                    />

                    {isGrouped ? (
                      <div class="flex flex-col gap-8 w-full z-20 px-4 py-2">
                        {rows.map((row, ri) => (
                          <div class="flex gap-1" key={ri}>
                            {row.map(([cat, items]) => (
                              <div class="submenu-category w-48 font-bold" key={cat}>
                                <div class="font-bold text-sm mb-1 capitalize">{cat}</div>
                                <div class="flex flex-col">
                                  {items.map((item, j) => (
                                    <sub-menu-item
                                      logoUrl={item.icon}
                                      isActive={this.activeRoute === item.key}
                                      item={item}
                                      key={item.id || item.slug || j}
                                      onMenuSelect={(e: CustomEvent<any>) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const consolidatedItem = { ...e.detail, mainMenu: menuItem.title };
                                        this.submenuClick.emit(consolidatedItem);
                                      }}
                                      submenuType={"secondary"}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        class={`grid ${mi.header_links!.length < 3
                          ? 'grid-rows-' + mi.header_links!.length
                          : 'grid-cols-2'
                          } ${mi.submenuType === 'primary' ? 'gap-2' : ''
                          } gap-2 w-full z-20`}
                      >
                        {mi.header_links!.map((item, j) => (
                          <sub-menu-item
                            logoUrl={item.icon}
                            isActive={this.activeRoute === item.key}
                            item={item}
                            key={j}
                            onMenuSelect={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              const consolidatedItem = { ...item, mainMenu: menuItem.title };
                              this.submenuClick.emit(consolidatedItem)
                            }}
                            submenuType={mi.submenuType}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                }
              />
            ))}
          </div>
        </div>
      </Host>
    );
  }
}
